import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, Check, ImageIcon, FolderOpen, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import type { MediaFile } from '../../types/cms';
import { formatSize } from '../../lib/imageCompression';

interface MediaPickerProps {
  isOpen: boolean;
  onClose: () => void;
  media: MediaFile[];
  apiBase: string;
  mode?: 'single' | 'multiple';
  initialSelected?: string[];
  onSelect: (urls: string[]) => void;
  onUpload?: (
    file: File,
    meta?: { category?: string; tags?: string[] }
  ) => Promise<string | undefined | void>;
  onDelete?: (id: string) => Promise<void>;
  usedMediaIds?: Set<string>;
  defaultCategory?: string;
  defaultTags?: string[];
  // 開啟時若清單為空，自動呼叫重抓（修復：mount 抓取偶發失敗後清單停在空陣列）
  onOpenRefresh?: () => void;
}

export const MediaPicker: React.FC<MediaPickerProps> = ({
  isOpen,
  onClose,
  media,
  apiBase,
  mode = 'multiple',
  initialSelected = [],
  onSelect,
  onUpload,
  onDelete,
  usedMediaIds,
  defaultCategory,
  defaultTags,
  onOpenRefresh,
}) => {
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [deletingFile, setDeletingFile] = useState<MediaFile | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 開啟瞬間若清單為空 → 自動重抓一次（mount 抓取失敗/未完成的補救）
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current && media.length === 0) {
      onOpenRefresh?.();
    }
    wasOpen.current = isOpen;
  }, [isOpen, media.length, onOpenRefresh]);

  // 每次開啟重置選擇（2026-08-28 修復：選擇狀態跨開關殘留，
  // 上次揀過的相會累積進下一次「加入」，造成加幾張變一大堆）
  useEffect(() => {
    if (isOpen) setSelected(initialSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleManualRefresh = async () => {
    if (!onOpenRefresh || refreshing) return;
    setRefreshing(true);
    try {
      onOpenRefresh();
      // 讓按鈕轉圈至少一小段時間，避免閃跳
      await new Promise((r) => setTimeout(r, 400));
    } finally {
      setRefreshing(false);
    }
  };

  if (!isOpen) return null;

  const getMediaUrl = (m: MediaFile) => `${apiBase}/api/media/${m.id}`;
  const getMediaIdFromUrl = (url: string) => {
    const match = url.match(/\/api\/media\/([^/]+)$/);
    return match ? match[1] : null;
  };

  const toggle = (url: string) => {
    if (mode === 'single') {
      setSelected([url]);
      return;
    }
    setSelected((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]));
  };

  const handleConfirm = () => {
    onSelect(selected);
    onClose();
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || !onUpload) return;
    setUploading(true);
    setUploadError('');
    setUploadProgress({ done: 0, total: files.length });

    const meta = { category: defaultCategory, tags: defaultTags };
    const fileArray = Array.from(files);
    let completed = 0;
    let failed = 0;
    let lastError = '';

    const results = await Promise.all(
      fileArray.map(async (file) => {
        try {
          const newUrl = await onUpload(file, meta);
          return { newUrl: newUrl || undefined, error: null as string | null };
        } catch (err) {
          return {
            newUrl: undefined,
            error: err instanceof Error ? err.message : '上傳失敗',
          };
        }
      })
    );

    const uploadedUrls: string[] = [];

    results.forEach((result) => {
      completed++;
      setUploadProgress({ done: completed, total: fileArray.length });
      if (typeof result.newUrl === 'string') {
        uploadedUrls.push(result.newUrl);
        if (mode === 'single') {
          setSelected([result.newUrl]);
        } else {
          setSelected((prev) => (prev.includes(result.newUrl) ? prev : [...prev, result.newUrl]));
        }
      } else {
        failed++;
        if (result.error) lastError = result.error;
      }
    });

    setTimeout(() => {
      setUploading(false);
      setUploadProgress(null);
    }, 300);

    if (failed > 0) {
      setUploadError(`${failed} 張上傳失敗${lastError ? `：${lastError}` : ''}`);
    }

    // 上傳全部成功後自動確認並關閉，減少多餘點擊
    if (failed === 0 && uploadedUrls.length > 0) {
      const newSelected =
        mode === 'single'
          ? uploadedUrls.slice(0, 1)
          : [...selected, ...uploadedUrls.filter((u) => !selected.includes(u))];
      onSelect(newSelected);
      onClose();
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDeleteClick = (e: React.MouseEvent, file: MediaFile) => {
    e.stopPropagation();
    if (usedMediaIds?.has(file.id)) return;
    setDeletingFile(file);
  };

  const confirmDelete = async () => {
    if (!deletingFile || !onDelete) return;
    const id = deletingFile.id;
    setDeletingFile(null);
    try {
      await onDelete(id);
    } catch {
      // error handling is done inside onDelete callback
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        <div className="flex h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 p-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900">媒體庫選圖</h3>
              <p className="text-sm text-slate-500">
                {mode === 'single' ? '選擇一張圖片' : `已選擇 ${selected.length} 張`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {onUpload && (
                <div className="flex flex-col items-end gap-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFileInput}
                    disabled={uploading}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex cursor-pointer items-center gap-2 rounded bg-blue-800 px-3 py-2 text-sm font-bold text-white hover:bg-blue-900 disabled:opacity-60"
                  >
                    <Upload className="h-4 w-4" />
                    {uploading
                      ? uploadProgress
                        ? `上傳中 ${uploadProgress.done}/${uploadProgress.total}`
                        : '上傳中...'
                      : '上傳新圖'}
                  </button>
                  {uploadError && (
                    <p className="max-w-[200px] text-right text-xs text-red-600">{uploadError}</p>
                  )}
                </div>
              )}
              <button
                onClick={onClose}
                className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {isDragging && (
            <div className="border-b border-blue-100 bg-blue-50 p-6 text-center text-blue-700">
              <FolderOpen className="mx-auto mb-2 h-8 w-8" />
              <p className="font-bold">放開即可上傳圖片</p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4">
            {media.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-slate-400">
                <ImageIcon className="mb-3 h-12 w-12" />
                <p>{refreshing ? '載入中...' : '媒體庫暫無圖片'}</p>
                {onOpenRefresh && (
                  <button
                    type="button"
                    onClick={handleManualRefresh}
                    disabled={refreshing}
                    className="mt-3 flex items-center gap-2 rounded border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    重新載入媒體庫
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                {media.map((file) => {
                  const url = getMediaUrl(file);
                  const isSelected = selected.includes(url);
                  const isUsed = usedMediaIds?.has(file.id);
                  return (
                    <button
                      key={file.id}
                      type="button"
                      onClick={() => toggle(url)}
                      className={`relative aspect-square overflow-hidden rounded-lg border-2 text-left transition-all ${
                        isSelected ? 'border-blue-800 ring-2 ring-blue-200' : 'border-slate-200 hover:border-blue-400'
                      }`}
                    >
                      <img
                        src={url}
                        alt={file.name}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                      {onDelete && (
                        <span
                          onClick={(e) => handleDeleteClick(e, file)}
                          title={isUsed ? '此圖片正被使用中，無法刪除' : '刪除圖片'}
                          className={`absolute left-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full text-white shadow-sm ${
                            isUsed
                              ? 'cursor-not-allowed bg-slate-400'
                              : 'cursor-pointer bg-red-600 hover:bg-red-700'
                          }`}
                        >
                          {isUsed ? (
                            <AlertTriangle className="h-3.5 w-3.5" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </span>
                      )}
                      {isSelected && (
                        <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-800 text-white">
                          <Check className="h-4 w-4" />
                        </div>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="truncate text-[10px] text-white">{file.name}</p>
                        <p className="text-[9px] text-white/80">{formatSize(file.size)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-100 p-4">
            <button
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
            >
              取消
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected.length === 0}
              className="rounded-lg bg-blue-800 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-900 disabled:opacity-50"
            >
              {mode === 'single' ? '確認選擇' : `加入 ${selected.length} 張`}
            </button>
          </div>
        </div>
      </div>

      {deletingFile && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">確認刪除</h3>
            <p className="mb-6 text-sm text-slate-600">
              確定要從媒體庫永久刪除「{deletingFile.name}」嗎？
              <br />
              <span className="text-xs text-slate-500">刪除後將無法復原，引用此圖片的地方會顯示破圖。</span>
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingFile(null)}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                確認刪除
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
